<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { getRegexp } from 'shared/helpers/Validators';
import { ATTRIBUTE_TYPES } from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

export default {
  components: {
    NextButton,
    Checkbox,
  },
  props: {
    selectedAttribute: {
      type: Object,
      default: () => {},
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onClose'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      attributeType: 0,
      regexPattern: null,
      regexCue: null,
      regexEnabled: false,
      show: true,
      attributeKey: '',
      values: [],
      options: [],
      isTouched: true,
      draggedIndex: null,
      syncWithLabels: false,
      defaultValue: '',
      wonValue: '',
      isDealAmount: false,
      editingIndex: null,
      editingValue: '',
      attributeValueRenames: [],
    };
  },
  validations: {
    displayName: {
      required,
    },
    attributeType: {
      required,
    },
    description: {
      required,
      minLength: minLength(1),
    },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'getUIFlags',
    }),
    types() {
      return ATTRIBUTE_TYPES.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${item.key}`),
      }));
    },
    setAttributeListValue() {
      return this.selectedAttribute.attribute_values
        ? this.selectedAttribute.attribute_values.map(item => ({
            name: item,
            originalName: item,
          }))
        : [];
    },
    updatedAttributeListValues() {
      return this.values.map(item => item.name);
    },
    defaultValueOptions() {
      return this.updatedAttributeListValues.filter(
        value => value.toLocaleLowerCase().trim() !== 'sin seleccionar'
      );
    },
    validAttributeValueRenames() {
      return this.attributeValueRenames.filter(rename =>
        this.updatedAttributeListValues.includes(rename.to)
      );
    },
    isButtonDisabled() {
      return this.v$.description.$invalid || this.isMultiselectInvalid;
    },
    isMultiselectInvalid() {
      return (
        this.isAttributeTypeList && this.isTouched && this.values.length === 0
      );
    },

    pageTitle() {
      return `${this.$t('ATTRIBUTES_MGMT.EDIT.TITLE')} - ${
        this.selectedAttribute.attribute_display_name
      }`;
    },
    selectedAttributeType() {
      return this.types.find(
        item =>
          item.key.toLowerCase() ===
          this.selectedAttribute.attribute_display_type
      )?.id;
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR');
    },
    isAttributeTypeList() {
      return this.attributeType === 6;
    },
    isAttributeTypeText() {
      return this.attributeType === 0;
    },
    canSyncWithLabels() {
      return (
        this.selectedAttribute.attribute_model === 'contact_attribute' &&
        this.isAttributeTypeList
      );
    },
    canConfigureWonValue() {
      const key = (this.selectedAttribute?.attribute_key || '').toLowerCase();
      return (
        this.selectedAttribute.attribute_model === 'contact_attribute' &&
        this.isAttributeTypeList &&
        key === 'estado_contacto'
      );
    },
    // El monto del negocio solo puede vivir en un atributo numerico de contacto
    canConfigureDealAmount() {
      return (
        this.selectedAttribute.attribute_model === 'contact_attribute' &&
        [1, 2].includes(this.attributeType)
      );
    },
    isRegexEnabled() {
      return this.regexEnabled;
    },
  },
  mounted() {
    this.setFormValues();
  },
  watch: {
    // Si el tipo deja de ser numerico, el atributo ya no puede ser el monto
    canConfigureDealAmount(canConfigure) {
      if (!canConfigure) this.isDealAmount = false;
    },
    defaultValueOptions(values) {
      if (this.defaultValue && !values.includes(this.defaultValue)) {
        this.defaultValue = '';
      }
      if (this.wonValue && !values.includes(this.wonValue)) {
        this.wonValue = '';
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('onClose');
    },
    addTagValue(tagValue) {
      const tag = {
        name: tagValue,
      };
      this.values.push(tag);
      this.$refs.tagInput.$el.focus();
    },
    setFormValues() {
      const regexPattern = this.selectedAttribute.regex_pattern
        ? getRegexp(this.selectedAttribute.regex_pattern).source
        : null;
      this.displayName = this.selectedAttribute.attribute_display_name;
      this.description = this.selectedAttribute.attribute_description;
      this.attributeType = this.selectedAttributeType;
      this.attributeKey = this.selectedAttribute.attribute_key;
      this.regexPattern = regexPattern;
      this.regexCue = this.selectedAttribute.regex_cue;
      this.regexEnabled = regexPattern != null;
      this.values = this.setAttributeListValue;
      this.syncWithLabels = Boolean(this.selectedAttribute.sync_with_labels);
      this.defaultValue = this.selectedAttribute.default_value || '';
      this.wonValue = this.selectedAttribute.won_value || '';
      this.isDealAmount = Boolean(this.selectedAttribute.is_deal_amount);
    },
    async editAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.regexEnabled) {
        this.regexPattern = null;
        this.regexCue = null;
      }
      try {
        await this.$store.dispatch('attributes/update', {
          id: this.selectedAttribute.id,
          attribute_description: this.description,
          attribute_display_name: this.displayName,
          attribute_values: this.updatedAttributeListValues,
          regex_pattern: this.regexPattern
            ? new RegExp(this.regexPattern).toString()
            : null,
          regex_cue: this.regexCue,
          sync_with_labels: this.canSyncWithLabels && this.syncWithLabels,
          default_value: this.defaultValue || null,
          won_value: this.wonValue || null,
          is_deal_amount: this.canConfigureDealAmount && this.isDealAmount,
          attribute_value_renames: this.validAttributeValueRenames,
        });
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.EDIT.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.EDIT.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
    toggleRegexEnabled() {
      this.regexEnabled = !this.regexEnabled;
    },
    onDragStart(event, index) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index);
    },
    onDragEnter(index) {
      if (this.draggedIndex === null || this.draggedIndex === index) return;
      const temp = this.values[this.draggedIndex];
      this.values.splice(this.draggedIndex, 1);
      this.values.splice(index, 0, temp);
      this.draggedIndex = index;
    },
    onDragEnd() {
      this.draggedIndex = null;
    },
    removeOption(index) {
      if (this.defaultValue === this.values[index].name) {
        this.defaultValue = '';
      }
      const originalName = this.values[index].originalName;
      this.attributeValueRenames = this.attributeValueRenames.filter(
        rename => rename.from !== originalName
      );
      this.values.splice(index, 1);
    },
    beginOptionEdit(index) {
      this.editingIndex = index;
      this.editingValue = this.values[index].name;
    },
    cancelOptionEdit() {
      this.editingIndex = null;
      this.editingValue = '';
    },
    saveOptionEdit(index) {
      const value = this.editingValue.trim();
      if (!value) return;

      const option = this.values[index];
      const previousValue = option.name;
      option.name = value;
      if (this.defaultValue === previousValue) {
        this.defaultValue = value;
      }
      this.attributeValueRenames = this.attributeValueRenames.filter(
        rename => rename.from !== option.originalName
      );
      if (option.originalName && option.originalName !== value) {
        this.attributeValueRenames.push({
          from: option.originalName,
          to: value,
        });
      }
      this.cancelOptionEdit();
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-col w-full" @submit.prevent="editAttributes">
      <div class="w-full">
        <woot-input
          v-model="displayName"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: v$.displayName.$error }"
          :error="
            v$.displayName.$error
              ? $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR')
              : ''
          "
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
          @blur="v$.displayName.$touch"
        />
        <woot-input
          v-model="attributeKey"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL')"
          type="text"
          :class="{ error: v$.attributeKey.$error }"
          :error="v$.attributeKey.$error ? keyErrorMessage : ''"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.PLACEHOLDER')"
          readonly
          @blur="v$.attributeKey.$touch"
        />
        <label :class="{ error: v$.description.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
          <textarea
            v-model="description"
            rows="5"
            type="text"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
          <span v-if="v$.description.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.ERROR') }}
          </span>
        </label>
        <label :class="{ error: v$.attributeType.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
          <select v-model="attributeType" disabled>
            <option v-for="type in types" :key="type.id" :value="type.id">
              {{ type.option }}
            </option>
          </select>
          <span v-if="v$.attributeType.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.ERROR') }}
          </span>
        </label>
        <div v-if="isAttributeTypeList" class="multiselect--wrap">
          <label>
            {{ $t('ATTRIBUTES_MGMT.EDIT.TYPE.LIST.LABEL') }}
          </label>
          <multiselect
            ref="tagInput"
            v-model="values"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.PLACEHOLDER')"
            label="name"
            track-by="name"
            :class="{ invalid: isMultiselectInvalid }"
            :options="options"
            multiple
            taggable
            @tag="addTagValue"
          />
          <label
            v-show="isMultiselectInvalid"
            class="text-n-ruby-9 dark:text-n-ruby-9 text-sm font-normal mt-1"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
          </label>
          <label
            v-if="canSyncWithLabels"
            class="flex items-center gap-2 mt-3 text-sm text-n-slate-12"
          >
            <Checkbox v-model="syncWithLabels" />
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.SYNC_WITH_LABELS') }}
          </label>
          <label v-if="canSyncWithLabels && syncWithLabels" class="mt-3">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.DEFAULT_VALUE') }}
            <select v-model="defaultValue">
              <option value="">
                {{
                  $t(
                    'ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.DEFAULT_VALUE_PLACEHOLDER'
                  )
                }}
              </option>
              <option
                v-for="value in defaultValueOptions"
                :key="value"
                :value="value"
              >
                {{ value }}
              </option>
            </select>
          </label>
          <label v-if="canConfigureWonValue" class="mt-3">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.WON_VALUE') }}
            <select v-model="wonValue">
              <option value="">
                {{
                  $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.WON_VALUE_PLACEHOLDER')
                }}
              </option>
              <option
                v-for="value in defaultValueOptions"
                :key="value"
                :value="value"
              >
                {{ value }}
              </option>
            </select>
          </label>
          <div v-if="values.length > 0" class="mt-4">
            <label
              class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block"
            >
              Orden de las opciones (Arrastra para reordenar):
            </label>
            <div class="flex flex-col gap-2">
              <div
                v-for="(item, index) in values"
                :key="item.name"
                :draggable="editingIndex !== index"
                class="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md cursor-move hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                @dragstart="onDragStart($event, index)"
                @dragover.prevent
                @dragenter.prevent="onDragEnter(index)"
                @dragend="onDragEnd"
                @drop="onDragEnd"
              >
                <div class="flex items-center gap-3">
                  <span
                    class="text-slate-400 dark:text-slate-500 cursor-grab active:cursor-grabbing"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 8h16M4 16h16"
                      />
                    </svg>
                  </span>
                  <input
                    v-if="editingIndex === index"
                    v-model="editingValue"
                    type="text"
                    class="!mb-0 !h-8 text-sm"
                    @keyup.enter.prevent="saveOptionEdit(index)"
                    @keyup.esc="cancelOptionEdit"
                  />
                  <span
                    v-else
                    class="text-sm text-slate-800 dark:text-slate-200 font-medium"
                  >
                    {{ item.name }}
                  </span>
                </div>
                <div class="flex items-center gap-1">
                  <button
                    v-if="editingIndex === index"
                    type="button"
                    class="i-lucide-check size-4 text-n-teal-11"
                    :aria-label="
                      $t('ATTRIBUTES_MGMT.EDIT.TYPE.LIST.SAVE_OPTION')
                    "
                    @click="saveOptionEdit(index)"
                  ></button>
                  <button
                    v-else
                    type="button"
                    class="i-lucide-pen size-4 text-n-slate-11"
                    :aria-label="
                      $t('ATTRIBUTES_MGMT.EDIT.TYPE.LIST.EDIT_OPTION')
                    "
                    @click="beginOptionEdit(index)"
                  ></button>
                  <button
                    type="button"
                    class="i-lucide-trash-2 size-4 text-n-ruby-11"
                    :aria-label="$t('ATTRIBUTES_MGMT.DELETE.BUTTON_TEXT')"
                    @click="removeOption(index)"
                  ></button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isAttributeTypeText">
          <input
            v-model="regexEnabled"
            type="checkbox"
            @input="toggleRegexEnabled"
          />
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
        </div>
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexPattern"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.LABEL')"
          type="text"
          :placeholder="
            $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
          "
        />
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexCue"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.LABEL')"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')"
        />
        <label v-if="canConfigureDealAmount" class="mt-3">
          <input v-model="isDealAmount" type="checkbox" />
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DEAL_AMOUNT.LABEL') }}
          <p class="mt-1 mb-0 text-xs text-n-slate-11">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DEAL_AMOUNT.HELP') }}
          </p>
        </label>
      </div>
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('ATTRIBUTES_MGMT.EDIT.UPDATE_BUTTON_TEXT')"
          :is-loading="isUpdating"
          :disabled="isButtonDisabled"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.key-value {
  padding: 0 0.5rem 0.5rem 0;
  font-family: monospace;
}

.multiselect--wrap {
  margin-bottom: 1rem;
}

:deep(.multiselect) {
  margin-bottom: 0;
}

:deep(.multiselect__content-wrapper) {
  display: none;
}

:deep(.multiselect--active) .multiselect__tags {
  border-radius: 0.3125rem;
}
</style>
